/* import __COLOCATED_TEMPLATE__ from './export-schedule-frequency-item.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import formatTimezoneToUTC from 'embercom/lib/format-timezone-to-utc';
import moment from 'moment-timezone';
import { cached } from 'tracked-toolbox';
import { action } from '@ember/object';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';
import { indexBy } from 'underscore';
import { SchedulePreset } from 'embercom/models/reporting/dataset-export-schedule';
import { isEmpty } from '@ember/utils';
interface Args {
  text: string;
  schedule: DatasetExportSchedule;
  timezone: string;
}

export default class ExportScheduleFrequencyItem extends Component<Args> {
  @service declare intl: any;
  @service declare appService: $TSFixMe;

  @tracked selectedTime = this.hours[0].value;

  get selectedCadence() {
    return this.args.schedule.exportRule.interval.step;
  }

  get frequencies() {
    let basePath = 'reporting.data-export.schedule-export.new-schedule.custom-frequency';
    return [
      {
        text: this.intl.t(`${basePath}.weeks`),
        value: SchedulePreset.WEEKLY,
      },
      {
        text: this.intl.t(`${basePath}.months`),
        value: SchedulePreset.MONTHLY,
      },
    ];
  }

  get daysOfWeek() {
    let days = [
      { text: this.intl.t('app.lib.reporting.week-days.sunday'), value: 0 },
      { text: this.intl.t('app.lib.reporting.week-days.monday'), value: 1 },
      { text: this.intl.t('app.lib.reporting.week-days.tuesday'), value: 2 },
      { text: this.intl.t('app.lib.reporting.week-days.wednesday'), value: 3 },
      { text: this.intl.t('app.lib.reporting.week-days.thursday'), value: 4 },
      { text: this.intl.t('app.lib.reporting.week-days.friday'), value: 5 },
      { text: this.intl.t('app.lib.reporting.week-days.saturday'), value: 6 },
    ];
    return days.map((day) => ({
      ...day,
      isSelected: this.args.schedule.exportRule.daysOfWeek.includes(day.value),
    }));
  }

  @cached
  get hours() {
    let timezoneString = formatTimezoneToUTC(this.args.timezone);
    return Array.from(Array(24), (_, i) => ({
      text: `${moment({ hour: i }).format('h:mmA')} ${timezoneString}`,
      value: i,
    }));
  }

  @action
  onDaysSelectionChange(value: number[]) {
    if (value.length > 0) {
      this.args.schedule.exportRule = {
        ...this.args.schedule.exportRule,
        daysOfWeek: value,
      };
    }
  }

  @action
  onStepSelectionChange(value: string) {
    let intervalValue = this.args.schedule.exportRule.interval.value;
    if (value === SchedulePreset.MONTHLY) {
      this.args.schedule.exportRule.interval = {
        step: value,
        value: intervalValue,
        dayPosition: 'last',
      };
    } else {
      this.args.schedule.exportRule.interval = {
        step: value,
        value: intervalValue,
      };
    }
  }

  @action
  onTimeSelectionChange(timeIndex: number) {
    this.args.schedule.exportRule.deliveryTime = {
      hour: timeIndex,
      minute: 0,
      timezone: this.args.timezone,
    };
  }

  get selectedDaysLabel() {
    let indexedDays = indexBy(this.daysOfWeek, 'value');
    let label = this.args.schedule.exportRule.daysOfWeek.map((day) => indexedDays[day].text);
    if (label.length > 2) {
      return `${label.slice(0, 4).join(', ')}...`;
    }
    return label.join(', ');
  }

  @action
  updateIntervalValue(e: Event) {
    let value = (e.target as HTMLInputElement).value;
    if (!isNaN(Number(value))) {
      this.args.schedule.exportRule = {
        ...this.args.schedule.exportRule,
        interval: {
          ...this.args.schedule.exportRule.interval,
          value: Number(value),
        },
      };
    }
  }

  get applyButtonDisabled() {
    return isEmpty(this.args.schedule.exportRule.interval.value);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ExportScheduleFrequencyItem': typeof ExportScheduleFrequencyItem;
    'reporting/custom/export-schedule-frequency-item': typeof ExportScheduleFrequencyItem;
  }
}
